import type { MouseEvent } from 'react';
import { useState } from 'react';

import { Box, IconButton, Tooltip, rem } from '@vimeo/bokeh';
import {
  Checkmark,
  Clipboard,
  ThumbUp,
  ThumbUpFilled,
  ThumbDown,
  ThumbDownFilled,
} from '@vimeo/bokeh-icons';

import { useGetSvvManageBpEvents } from '../../bp-events';
import * as final from '../../copy/final';
import { useBokehToast } from '../../hooks/useBokehToast';

export type QuickActionsRatedFeature =
  | 'ask_ai'
  | 'chapters'
  | 'highlights'
  | 'video_description'
  | 'video_tags'
  | 'video_title';

interface QuickActionsProps {
  /**
   * If true, the tooltip will show "Replace" instead of "Save".
   */
  isReplacing?: boolean;
  /**
   * If true, the save/replace button will be in a loading state.
   */
  isSaving?: boolean;
  onCopyClick?: VoidFunction;
  onSaveClick?: VoidFunction;
  /**
   * BP event whenever someone clicks on the thumbs up.
   */
  onThumbsUpClick?: VoidFunction;
  /**
   * BP event whenever someone clicks on the thumbs down.
   */
  onThumbsDownClick?: VoidFunction;
  /**
   * Explicit generation ID to attach to the thumbs-rate event.
   * When provided, takes precedence over the latest ID in the store.
   */
  generationId?: string | null;
  /**
   * The feature that is being rated
   */
  ratedFeature: QuickActionsRatedFeature;

  /**
   * If true, the save and copy buttons will be shown.
   */
  showCopy?: boolean;
  showSave?: boolean;
  showThumbs?: boolean;
}

type ThumbOptions = 'thumbs-up' | 'thumbs-down';
type ThumbOptionsOrNone = ThumbOptions | 'none';

/**
 * A component that allows users to rate something with thumbs up or thumbs down.
 * Manages state internally: if you click on one, it's selected. If one is selected and you click on another, you set it to the second option. Clicking a selected one deselects.
 * Pass in BP events to handle the clicks.
 */
export const QuickActions = ({
  isReplacing,
  isSaving,
  onCopyClick,
  onSaveClick,
  onThumbsDownClick,
  onThumbsUpClick,
  generationId,
  ratedFeature,
  showSave = false,
  showCopy = false,
  showThumbs = false,
}: QuickActionsProps) => {
  const [thumbSelected, setThumbSelected] = useState<ThumbOptionsOrNone>('none');
  const { sendThumbsOnVideoTitleClickEvent } = useGetSvvManageBpEvents();
  const notify = useBokehToast();

  const handleThumbClick = (event: MouseEvent<HTMLElement>) => {
    const thumbClicked = event.currentTarget.dataset.thumb as ThumbOptions;

    if (thumbClicked === thumbSelected) {
      return;
    }

    if (thumbClicked === 'thumbs-up') {
      onThumbsUpClick?.();
      sendThumbsOnVideoTitleClickEvent(ratedFeature, generationId ?? null, true);
    }
    if (thumbClicked === 'thumbs-down') {
      onThumbsDownClick?.();
      sendThumbsOnVideoTitleClickEvent(ratedFeature, generationId ?? null, false);
    }

    if (thumbClicked !== thumbSelected) {
      notify?.(final.feedback);
    }

    setThumbSelected((prev) => (prev === thumbClicked ? 'none' : thumbClicked));
  };

  const handleCopyClick = () => {
    onCopyClick?.();
    notify?.(final.copied);
  };

  return (
    <Box
      display="flex"
      gap={rem(4)}
      padding="0"
      minHeight={rem(24)}
      justifyContent="center"
      alignItems="center"
    >
      {showSave && (
        <Tooltip label={isReplacing ? final.replace : final.save} placement={'top'}>
          <IconButton
            aria-label={isReplacing ? final.replace : final.save}
            icon={
              <Checkmark
                color="text-secondary"
                _hover={{
                  color: 'text-primary',
                }}
              />
            }
            isLoading={isSaving}
            onClick={onSaveClick}
            size="xs"
            variant="tertiary"
          />
        </Tooltip>
      )}
      {showCopy && (
        <Tooltip label={final.copy} placement={'top'}>
          <IconButton
            aria-label={final.copy}
            icon={
              <Clipboard
                color="text-secondary"
                _hover={{
                  color: 'text-primary',
                }}
              />
            }
            variant="tertiary"
            size="xs"
            onClick={handleCopyClick}
          />
        </Tooltip>
      )}
      {showThumbs && (
        <>
          <Tooltip label={final.good} placement={'top'}>
            <IconButton
              aria-label={final.good}
              data-thumb="thumbs-up"
              icon={
                thumbSelected === 'thumbs-up' ? (
                  <ThumbUpFilled
                    color="text-secondary"
                    _hover={{
                      color: 'text-primary',
                    }}
                  />
                ) : (
                  <ThumbUp
                    color="text-secondary"
                    _hover={{
                      color: 'text-primary',
                    }}
                  />
                )
              }
              onClick={handleThumbClick}
              variant="tertiary"
              size="xs"
            />
          </Tooltip>
          <Tooltip label={final.bad} placement={'top'}>
            <IconButton
              aria-label={final.bad}
              data-thumb="thumbs-down"
              icon={
                thumbSelected === 'thumbs-down' ? (
                  <ThumbDownFilled color="text-secondary" />
                ) : (
                  <ThumbDown
                    color="text-secondary"
                    _hover={{
                      color: 'text-primary',
                    }}
                  />
                )
              }
              onClick={handleThumbClick}
              variant="tertiary"
              size="xs"
            />
          </Tooltip>
        </>
      )}
    </Box>
  );
};
